import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getRouteMutationKey } from '.';
import { stopService } from '../../../../application/StopService';
import { ReportStatus } from '../../../../constants/Stops';
import { ImageUploadedProps, Stop } from '../../../../domain/stops/Stop';
import { useErrorMiddleware } from '../../../../hooks/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../../hooks/useShowSnackbarMessage';
import { CreateReportCancelProps, CreateReportCompleteProps } from '../../../../repositories/stop/StopRepository';
import { useRetrieveVehicle } from '../../hooks/useRetrieveVehicle';
import { useLocationContext } from '../../locationContext/context';
import { useRouteContext } from '../context/context';
import { useStartStop } from './useStartStop';

interface UseCreateStopReportProps {
  status: ReportStatus;
  stop: Stop;
  images?: Array<ImageUploadedProps>;
  reportData?: CreateReportCompleteProps | CreateReportCancelProps;
}

type UseCreateStopReportResponseProps = ServiceResponse<
  ({ status, stop, images, reportData }: UseCreateStopReportProps) => void,
  void
>;

// Hooks act as 'Adapter' layer.
export const useCreateStopReport = (): UseCreateStopReportResponseProps => {
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { data: vehicleData } = useRetrieveVehicle();
  const { locationState } = useLocationContext();
  const { routeState } = useRouteContext();
  const queryClient = useQueryClient();

  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { timeInStop: counter, clearReportDate } = useStartStop();

  const { isLoading, isSuccess, isError, mutate } = useMutation<Stop, unknown, UseCreateStopReportProps>(
    ({ status, stop, images, reportData }: UseCreateStopReportProps): Promise<Stop> => {
      if (!routeState.route?.project_id) {
        throw new Error(`routeState.route?.project_id does not exist`);
      }

      return stopService.createReport({
        currentPosition: locationState.currentPosition,
        status,
        stop,
        images,
        reportData,
        timeInStop: counter,
        project: vehicleData!.project,
      });
    },
    {
      onSuccess: (updatedStop) => {
        queryClient.invalidateQueries(getRouteMutationKey({ routeId: updatedStop.route_id }));
        clearReportDate();
      },
      // If the mutation fails, use the context we returned above
      onError: (err: any, _variables: any, context: any) => {
        console.info(`Create Stop Report Error: `, err?.response);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }

        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  const createReport = ({ status, stop, images, reportData }: UseCreateStopReportProps) => {
    mutate({
      status,
      stop,
      images,
      reportData,
    });
  };

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: createReport,
  };
};
